// ── Active systems: what each one is, its NHC formation odds, its motion ──
// Pure helpers behind the map's storm symbols and the "Active systems" list.

import type { GtwoFeature, StormRef } from "../data/liveApi";
import { lonNear } from "./geo";
import { utcMs } from "./intensity";

export type SystemKind = "major" | "hurricane" | "storm" | "depression" | "subtropical" | "post" | "invest" | "potential";

export interface SystemClass {
  kind: SystemKind;
  label: string; // "Major Hurricane", "Tropical Storm", …
  short: string; // "Cat 4", "TS", "TD", "Invest"
  cat: number | null; // Saffir-Simpson 1–5 for hurricanes
}

function ssCat(vmax: number): number {
  return vmax >= 137 ? 5 : vmax >= 113 ? 4 : vmax >= 96 ? 3 : vmax >= 83 ? 2 : 1;
}

// ATCF system types that make a fix a tropical (or subtropical) cyclone
export const TC_TYPES = new Set(["TD", "TS", "HU", "TY", "ST", "TC", "SD", "SS"]);

/** A numbered system that has never been a tropical cyclone: NHC's Potential
 *  Tropical Cyclone, with advisories (cone, watches, warnings) before it
 *  forms. Its fixes read DB/LO, like a remnant's. */
export function isPotential(ref: StormRef): boolean {
  return !ref.is_invest && ref.latest?.ever_tc === false;
}

/** The same, judged from a storm bundle's own best track (an invest number
 *  never counts; a track with no known types neither). */
export function trackIsPotential(stormId: string, types: (string | null | undefined)[]): boolean {
  const num = parseInt(stormId.slice(2, 4), 10);
  const known = types.filter((t): t is string => !!t).map((t) => t.toUpperCase());
  return num < 90 && known.length > 0 && !known.some((t) => TC_TYPES.has(t));
}

/** The storm an id stands for now: an invest NHC has designated (dropped
 *  from the list) is followed to the storm it became. */
export function currentStormId(id: string, index: StormRef[]): string {
  if (index.some((s) => s.storm_id === id)) return id;
  return index.find((s) => s.formerly?.includes(id.toLowerCase()))?.storm_id ?? id;
}

/** Potential by the storm list's word when it has the storm (it follows NHC's
 *  advisories, which run ahead of the best track), else by the track. */
export function stormIsPotential(stormId: string, types: (string | null | undefined)[], ref?: StormRef | null): boolean {
  return ref?.latest && !ref.is_invest ? isPotential(ref) : trackIsPotential(stormId, types);
}

/** Classification from the newest fix's ATCF type, winds as the tiebreak. */
export function systemClass(ref: StormRef): SystemClass {
  const v = ref.latest?.vmax ?? 0;
  const t = (ref.latest?.type ?? "").toUpperCase();
  if (ref.is_invest) return { kind: "invest", label: "Invest", short: "Invest", cat: null };
  if (isPotential(ref)) return { kind: "potential", label: "Potential Tropical Cyclone", short: "PTC", cat: null };
  if (t === "SS") return { kind: "subtropical", label: "Subtropical Storm", short: "SS", cat: null };
  if (t === "SD") return { kind: "subtropical", label: "Subtropical Depression", short: "SD", cat: null };
  if (t === "EX") return { kind: "post", label: "Post-tropical Cyclone", short: "Post-trop.", cat: null };
  if (t === "LO" || t === "DB" || t === "WV" || t === "IN") return { kind: "post", label: "Remnant Low", short: "Low", cat: null };
  if (v >= 64 || t === "HU" || t === "TY" || t === "ST") {
    const c = ssCat(v);
    return { kind: c >= 3 ? "major" : "hurricane", label: c >= 3 ? "Major Hurricane" : "Hurricane", short: `Cat ${c}`, cat: c };
  }
  if (v >= 34 || t === "TS") return { kind: "storm", label: "Tropical Storm", short: "TS", cat: null };
  return { kind: "depression", label: "Tropical Depression", short: "TD", cat: null };
}

const titleCase = (s: string) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const BASIN_SUFFIX: Record<string, string> = { AL: "L", EP: "E", CP: "C" };

/** "Nolo", "Invest 91L", "PTC One", "EP18" */
export function systemName(ref: StormRef): string {
  const suffix = BASIN_SUFFIX[(ref.basin ?? "AL").toUpperCase()] ?? "";
  if (ref.is_invest) return `Invest ${ref.number ?? ""}${suffix}`;
  if (isPotential(ref)) {
    return `PTC ${ref.name ? titleCase(ref.name) : `${(ref.basin ?? "").toUpperCase()}${String(ref.number ?? "").padStart(2, "0")}`}`;
  }
  if (ref.name) return titleCase(ref.name);
  return `${(ref.basin ?? "").toUpperCase()}${String(ref.number ?? "").padStart(2, "0")}`;
}

export interface FormationOdds {
  prob2: number | null; // 48-hour formation chance, %
  prob7: number | null; // 7-day
  point: GtwoFeature | null; // the NHC ✕ it matched (so the map can drop the duplicate)
}

export function inRing(lon: number, lat: number, ring: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    const x = lonNear(xi, lon), xx = lonNear(xj, lon);
    if ((yi > lat) !== (yj > lat) && lon < ((xx - x) * (lat - yi)) / (yj - yi) + x) inside = !inside;
  }
  return inside;
}

/** An invest's official NHC formation odds: the outlook's ✕ disturbance
 *  marker nearest its position (within 6°), else the outlook area it sits in. */
export function formationOdds(ref: StormRef, gtwo: GtwoFeature[] | undefined): FormationOdds | null {
  const f = ref.latest;
  if (!ref.is_invest || !f || !gtwo?.length) return null;
  let best: GtwoFeature | null = null, bestD = Infinity;
  for (const g of gtwo) {
    if (g.kind !== "point" || g.lat == null || g.lon == null) continue;
    const d = Math.hypot((lonNear(g.lon, f.lon) - f.lon) * Math.cos((f.lat * Math.PI) / 180), g.lat - f.lat);
    if (d < bestD) { best = g; bestD = d; }
  }
  if (best && bestD <= 6) return { prob2: best.prob2, prob7: best.prob7, point: best };
  const area = gtwo.find((g) => g.kind === "area" && g.ring && g.ring.length >= 3 && inRing(f.lon, f.lat, g.ring));
  return area ? { prob2: area.prob2, prob7: area.prob7, point: null } : null;
}

const POINTS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
export const compass = (deg: number) => POINTS[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16];

/** "Moving NW at 9 kt" / "Nearly stationary", from the fixes 12 h apart. */
export function motionText(ref: StormRef): string | null {
  const f = ref.latest;
  if (f?.move_kt == null || f.move_deg == null) return null;
  return f.move_kt < 2 ? "Nearly stationary" : `Moving ${compass(f.move_deg)} at ${f.move_kt} kt`;
}

/** "3 h" / "2 d" */
export function ageText(hours: number): string {
  return hours < 1 ? "<1 h" : hours < 48 ? `${Math.round(hours)} h` : `${Math.round(hours / 24)} d`;
}

/** A fix older than this reads as stale (NHC fixes every 6 h). */
export const STALE_FIX_H = 12;

/** Hours since the newest fix (null if unknown). */
export function fixAgeHours(ref: StormRef, now = Date.now()): number | null {
  const t = utcMs(ref.latest?.dt ?? ref.last_fix ?? null);
  return t == null ? null : (now - t) / 3_600_000;
}

const BASIN_ORDER: Record<string, number> = { AL: 0, EP: 1, CP: 2 };

/** Atlantic, then East, then Central Pacific; strongest first, invests last. */
export function sortSystems(refs: StormRef[]): StormRef[] {
  return [...refs].sort((a, b) =>
    (BASIN_ORDER[(a.basin ?? "AL").toUpperCase()] ?? 9) - (BASIN_ORDER[(b.basin ?? "AL").toUpperCase()] ?? 9)
    || Number(!!a.is_invest) - Number(!!b.is_invest)
    || (b.latest?.vmax ?? 0) - (a.latest?.vmax ?? 0));
}
